import React from 'react';
import { Link } from 'react-router-dom';
import useFadeIn from '../hooks/useFadeIn';
import { buildSignupPath, buildSignupState, captureCtaClick } from '../utils/analytics';

const solutions = [
  {
    title: 'Transparency',
    description: 'Immediate, easy-to-read coverage information at the point of care.',
    gradient: 'from-blue-500 to-cyan-400',
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/>
        <rect x="8" y="2" width="8" height="4" rx="1"/>
        <path d="M9 14l2 2 4-4"/>
      </svg>
    ),
  },
  {
    title: 'Efficiency',
    description: 'Less manual admin work and fewer delays for prescribing teams.',
    gradient: 'from-violet-500 to-purple-400',
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
  },
  {
    title: 'Affordability',
    description: 'Clear price visibility to support more cost-effective medication decisions.',
    gradient: 'from-fuchsia-500 to-pink-400',
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.5 1.5l-8.5 8.5a4.95 4.95 0 007 7l8.5-8.5a4.95 4.95 0 00-7-7z"/>
        <line x1="6.5" y1="13" x2="13" y2="6.5"/>
        <path d="M18 16v6M15 19h6"/>
      </svg>
    ),
  },
];

function SolutionsSection() {
  const [headerRef, headerVisible] = useFadeIn();
  const [cardsRef, cardsVisible] = useFadeIn(0.08);

  return (
    <section className="bg-white px-8 md:px-16 pt-16 pb-10 md:pt-24 md:pb-12">
      <div className="max-w-[1440px] mx-auto flex flex-col gap-14">

        {/* Bridge */}
        <div className="flex justify-center">
          <p className="text-sm md:text-[15px] text-[#6e6e73] text-center max-w-2xl leading-relaxed font-normal">
            From coverage friction to a clearer prescribing workflow.
          </p>
        </div>

        {/* Header */}
        <div
          ref={headerRef}
          className={`fade-in-section ${headerVisible ? 'is-visible' : ''} flex flex-col items-center text-center gap-5 max-w-3xl mx-auto`}
        >
          <h2 className="text-4xl md:text-5xl xl:text-[56px] text-[#1d1d1f] leading-tight">
            Hello MedHug
          </h2>
          <p className="text-lg text-[#6e6e73] leading-relaxed font-normal">
            Instant insurance coverage information and cost-effective prescribing options — reducing delays and making medications more accessible.
          </p>
        </div>

        {/* Cards */}
        <div
          ref={cardsRef}
          className={`stagger-children ${cardsVisible ? 'is-visible' : ''} grid grid-cols-1 md:grid-cols-3 gap-6`}
        >
          {solutions.map((item, i) => (
            <div
              key={i}
              className="stagger-child group flex flex-col gap-4 p-6 md:gap-5 md:p-10 rounded-3xl bg-[#f5f5f7] hover:bg-[#ebebee] transition-all duration-500 cursor-default"
            >
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.gradient} flex items-center justify-center shadow-lg shadow-black/10`}>
                {item.icon}
              </div>
              <h3 className="text-[22px] font-semibold text-[#1d1d1f] mt-1">
                {item.title}
              </h3>
              <p className="text-[15px] text-[#6e6e73] leading-relaxed font-normal">
                {item.description}
              </p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="flex justify-center pt-2">
          <Link
            to={buildSignupPath()}
            state={buildSignupState('solutions-book-demo')}
            data-ph-capture-attribute-cta="solutions-book-demo"
            onClick={() => captureCtaClick('solutions-book-demo', { placement: 'solutions' })}
            className="text-[#06c] text-[15px] font-medium hover:text-[#004aad] transition-colors"
          >
            Book a Demo →
          </Link>
        </div>

      </div>
    </section>
  );
}

export default SolutionsSection;
